import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Form, 
  Input, 
  Table, 
  Tooltip, 
  Pagination, 
  Card, 
  message, 
  Modal 
} from 'antd';
import { SearchOutlined, FilePdfOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import 'antd/dist/reset.css'; // 仅为此页面导入 Ant Design 样式
import API from '../services/api'; // 导入统一的API实例
import { saveAs } from 'file-saver';
import { useLocation } from 'react-router-dom';
import usePermissionCheck from '../hooks/usePermissionCheck';
import PermissionDenied from '../components/PermissionDenied';

const UserAttackQuery = () => {
  const { hasPermission, isLoading: permissionLoading, userAccountType, userPermissions } = usePermissionCheck('canViewAttackRecords');
  
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const ipFromUrl = params.get('ip') || '';
  const [ip, setIp] = useState(ipFromUrl);
  const [attackLogs, setAttackLogs] = useState([]); // 存储攻击日志数据
  const [loading, setLoading] = useState(false); // 控制加载动画
  const [currentPage, setCurrentPage] = useState(1); // 当前页码
  const [exportLoading, setExportLoading] = useState(false); // 控制导出按钮加载状态
  const [showExportModal, setShowExportModal] = useState(false); // 控制导出确认弹窗
  const logsPerPage = 10; // 每页显示的日志数量
  const [hasSearched, setHasSearched] = useState(false); // 控制是否显示查询结果

  // 检查是否有导出权限 - 主账号默认有所有权限
  const hasExportPermission = userAccountType === 'main' || userPermissions?.canExportReports || false;

  // IP地址格式验证
  const validateIP = (ip) => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  useEffect(() => {
    setIp(ipFromUrl);
  }, [ipFromUrl]);

  // 权限检查 - 移到所有hooks之后
  if (!hasPermission) {
    return (
      <PermissionDenied 
        isLoading={permissionLoading}
        permissionName="攻击记录"
        userAccountType={userAccountType}
      />
    );
  }

  // 查询攻击日志的处理函数
  const handleAttackQuery = async () => {
    const trimmedIp = ip.trim();
    
    if (!trimmedIp) {
      message.error('请输入IP地址');
      return;
    }
    
    if (!validateIP(trimmedIp)) {
      message.error('请输入正确的IP地址格式（如：192.168.1.1）');
      return;
    }

    setLoading(true); // 开启加载动画
    setHasSearched(false); // 重置查询状态
    try {
      // 调用API获取攻击日志数据
      const response = await API.get('/api/user/ip-attack-lookup', {
        params: { ip: trimmedIp },  // 传递验证过的IP地址作为查询参数
      });

      // 检查响应数据结构
      if (response.data.message && !response.data.attackLogs) {
        // 后端返回消息表示没有找到数据
        message.info({
          content: `未找到IP地址 ${trimmedIp} 的攻击日志记录`,
          duration: 4
        });
        setAttackLogs([]);
      } else {
        const logs = response.data.attackLogs || [];
        if (logs.length === 0) {
          message.info({
            content: `未找到IP地址 ${trimmedIp} 的攻击日志记录`,
            duration: 4
          });
          setAttackLogs([]);  // 如果没有匹配的数据，清空attackLogs
        } else {
          setAttackLogs(logs); // 保存匹配到的日志数据
          setCurrentPage(1); // 每次查询后重置页码为1
          message.success(`查询成功，找到 ${logs.length} 条攻击日志`);
        }
      }
      setHasSearched(true); // 标记为已进行查询
    } catch (error) {
      console.error('获取攻击日志时出错:', error);
      
      // 直接显示后端返回的错误内容
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.request) {
        message.error('网络连接失败，请检查网络状态');
      } else {
        message.error('查询出错，请稍后重试');
      }
      
      // 发生真正错误时也要标记为已搜索，以便显示错误状态
      setHasSearched(true);
      setAttackLogs([]);
    }
    setLoading(false); // 关闭加载动画
  };

  // 修改持续时间转换函数，确保秒数显示
  const convertDurationToChineseWithSeconds = (duration) => {
    // 支持格式如 "1day2hours3minutes4seconds"
    const regex = /(\d+)(days?|hours?|minutes?|seconds?)/g;
    let result = '';
    let match;
    while ((match = regex.exec(duration)) !== null) {
      const value = match[1];
      const unit = match[2];
      switch (unit) {
        case 'day':
        case 'days':
          result += `${value}天 `;
          break;
        case 'hour':
        case 'hours':
          result += `${value}小时 `;
          break;
        case 'minute':
        case 'minutes':
          result += `${value}分钟 `;
          break;
        case 'second':
        case 'seconds':
          result += `${value}秒`;
          break;
        default:
          result += match[0];
      }
    }
    return result.trim() || duration;
  };

  // 将Mbps流量转换为Gbps
  const convertToGbps = (sizeInMbps) => {
    return (sizeInMbps / 1000).toFixed(2); // 转换为Gbps，保留两位小数
  };

  const formatDateTimeWithoutMilliseconds = (dateTimeStr) => {
    return dateTimeStr.split('.')[0];  // 只取.之前的部分，去掉毫秒
  };  

  // 将字节流量转换为Gbps
  const convertToGbpsFromBytes = (sizeInBytes) => {
    const sizeInBits = sizeInBytes * 8; // 转换为比特
    const sizeInMbps = sizeInBits / 1e6; // 转换为Mbps
    return (sizeInMbps / 1000).toFixed(2); // 转换为Gbps，保留两位小数
  };

  // 计算拦截率
  const calculateInterceptRate = (size, dropSize) => {
    const peakTrafficGbps = convertToGbps(size);
    const dropTrafficGbps = convertToGbpsFromBytes(dropSize);
    
    if (peakTrafficGbps === 0) return '0.00%';
    
    // 计算拦截率并保留两位小数，不进行进位
    let interceptRate = (dropTrafficGbps / peakTrafficGbps) * 100;
    
    // 将拦截率转换为字符串，并保留两位小数，防止进位
    interceptRate = interceptRate.toString();
    const dotIndex = interceptRate.indexOf('.');
    
    // 如果有小数点，截取小数点后两位；如果没有小数点，则补充".00"
    if (dotIndex !== -1) {
      interceptRate = interceptRate.substring(0, dotIndex + 3); // 保留两位小数
    } else {
      interceptRate = interceptRate + '.00'; // 没有小数点时补充.00
    }
    
    return `${interceptRate}%`;
  };

  // 分页逻辑，显示所有日志
  const indexOfLastLog = currentPage * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;
  const currentLogs = attackLogs.slice(indexOfFirstLog, indexOfLastLog);

  const totalPages = Math.ceil(attackLogs.length / logsPerPage);

  // 导出PDF功能
  const handleExportClick = () => {
    if (attackLogs.length === 0) {
      message.error('没有数据可生成');
      return;
    }
    
    // 检查是否有导出权限
    if (!hasExportPermission) {
      message.error('您的RAM账号没有导出攻击报告的权限，请联系主账号管理员申请相关权限');
      return;
    }
    
    setShowExportModal(true);
  };

  const handleExportConfirm = () => {
    setShowExportModal(false);
    setExportLoading(true);
    
    // 调用后端API进行PDF导出
    const exportData = {
      ip: ip,
      exportType: 'full',
      compress: true
    };
    
    // 使用Blob API处理二进制响应
    API.post('/api/user/export-attack-logs-pdf', exportData, { responseType: 'blob' })
      .then(response => {
        // 创建下载链接
        const blob = new Blob([response.data], { type: 'application/pdf' });
        
        // 获取当前北京时间用于文件名
        const date = new Date();
        const beijingTime = new Date(date.getTime() + (8 * 60 * 60 * 1000)); // 转换为北京时间
        const formattedDate = `${beijingTime.getUTCFullYear()}-${String(beijingTime.getUTCMonth() + 1).padStart(2, '0')}-${String(beijingTime.getUTCDate()).padStart(2, '0')}`;
        
        // 生成6位随机字符
        const randomStr = Math.random().toString(36).substring(2, 8);
        
        const fileName = `DDoS_Attack_Report_${ip.replace(/\./g, '_')}_${formattedDate}_${randomStr}.pdf`;
        
        // 使用file-saver库保存文件
        saveAs(blob, fileName);
        message.success('PDF报告生成成功');
        
        setExportLoading(false);
      })
      .catch(error => {
        console.error('导出PDF失败:', error);
        let errorMsg = '导出PDF失败';
        
        // 尝试解析错误消息（如果是JSON格式）
        if (error.response && error.response.data) {
          try {
            // 对于Blob类型的错误响应，需要先读取内容
            const reader = new FileReader();
            reader.onload = function() {
              try {
                const errorData = JSON.parse(reader.result);
                message.error(errorData.message || errorMsg);
              } catch (e) {
                message.error(errorMsg);
              }
            };
            reader.readAsText(error.response.data);
          } catch (e) {
            message.error(errorMsg);
          }
        } else {
          message.error(errorMsg);
        }
        
        setExportLoading(false);
      });
  };

  // 表格列定义
  const columns = [
    {
      title: '目标IP',
      dataIndex: 's_ip',
      key: 's_ip',
      width: 130,
      align: 'center',
    },
    {
      title: '目的端口',
      dataIndex: 's_port_list',
      key: 's_port_list',
      width: 90,
      align: 'center',
      render: (text) => (
        <Tooltip title={text} placement="top">
          <span style={{ cursor: 'pointer' }}>
            {text.length > 8 ? `${text.slice(0, 6)}...` : text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '攻击源IP',
      dataIndex: 'c_ip_list',
      key: 'c_ip_list',
      width: 120,
      align: 'center',
      render: (text) => (
        <Tooltip title={text} placement="top">
          <span style={{ cursor: 'pointer' }}>
            {text.length > 15 ? `${text.slice(0, 12)}...` : text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '源端口',
      dataIndex: 'c_port_list',
      key: 'c_port_list',
      width: 90,
      align: 'center',
      render: (text) => (
        <Tooltip title={text} placement="top">
          <span style={{ cursor: 'pointer' }}>
            {text.length > 8 ? `${text.slice(0, 6)}...` : text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '攻击状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      align: 'center',
      render: (status) => (
        <span 
          style={{ 
            color: status === 1 ? '#ff4d4f' : '#52c41a',
            fontWeight: 500
          }}
        >
          {status === 1 ? '正在攻击' : '攻击结束'}
        </span>
      ),
    },
    {
      title: '防护方式',
      dataIndex: 'deftype',
      key: 'deftype',
      width: 250,
      align: 'center',
    },
    {
      title: '服务器峰值流量',
      dataIndex: 'size',
      key: 'size',
      width: 120,
      align: 'center',
      render: (size) => (
        <span style={{ fontWeight: 500, color: '#1890ff' }}>
          {convertToGbps(size)} Gbps
        </span>
      ),
    },
    {
      title: '拦截峰值流量',
      dataIndex: 'drop_size',
      key: 'drop_size',
      width: 120,
      align: 'center',
      render: (dropSize) => (
        <span style={{ fontWeight: 500, color: '#52c41a' }}>
          {convertToGbpsFromBytes(dropSize)} Gbps
        </span>
      ),
    },
    {
      title: '拦截率',
      key: 'intercept_rate',
      width: 90,
      align: 'center',
      render: (_, record) => {
        const rate = calculateInterceptRate(record.size, record.drop_size);
        const percentage = parseFloat(rate.replace('%', ''));
        const color = percentage > 80 ? '#52c41a' : percentage > 50 ? '#faad14' : '#ff4d4f';
        return (
          <span style={{ fontWeight: 500, color }}>
            {rate}
          </span>
        );
      },
    },
    {
      title: '包类型',
      dataIndex: 'packet_type',
      key: 'packet_type',
      width: 90,
      align: 'center',
    },
    {
      title: '开始时间',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 160,
      align: 'center',
      render: (time) => formatDateTimeWithoutMilliseconds(time),
    },
    {
      title: '结束时间',
      dataIndex: 'end_time',
      key: 'end_time',
      width: 160,
      align: 'center',
    },
    {
      title: '持续时间',
      dataIndex: 'duration',
      key: 'duration',
      width: 130,
      align: 'center',
      render: (duration) => (
        <span style={{ fontWeight: 500 }}>
          {convertDurationToChineseWithSeconds(duration)}
        </span>
      ),
    },
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      handleAttackQuery();
    }
  };

  return (
    <div style={{ 
      padding: '20px', 
      backgroundColor: '#fafafa', 
      minHeight: 'calc(100vh - 64px)'
    }}>
      <div style={{ maxWidth: '100%', margin: '0 auto' }}>
        {/* 页面标题 */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '20px',
          background: '#fff',
          padding: '16px 20px',
          borderRadius: '8px',
          border: '1px solid #e8e8e8',
          maxWidth: '600px',
          margin: '0 auto 20px auto'
        }}>
          <h1 style={{ 
            margin: 0, 
            fontSize: '24px', 
            fontWeight: '500',
            color: '#262626'
          }}>
            攻击记录查询
          </h1>
        </div>

        {/* 提示信息 */}
        <Card 
          style={{ 
            marginBottom: '12px',
            border: '1px solid #91d5ff',
            background: '#e6f7ff',
            maxWidth: '600px',
            margin: '0 auto 12px auto'
          }}
          bodyStyle={{ padding: '8px 16px' }}
        >
          <div style={{ 
            textAlign: 'center', 
            color: '#0050b3',
            fontSize: '14px'
          }}>
            📅 查询数据为近三天数据
          </div>
        </Card>
        
        {/* 搜索区域 */}
        <Card 
          style={{ 
            marginBottom: '20px',
            border: '1px solid #e8e8e8',
            maxWidth: '600px',
            margin: '0 auto 20px auto'
          }}
          bodyStyle={{ padding: '16px' }}
        >
          <Form layout="inline" style={{ justifyContent: 'center' }}>
            <Form.Item>
                              <Input.Search
                  placeholder="请输入要查询的IP地址"
                  value={ip}
                  onChange={(e) => setIp(e.target.value)}
                  onSearch={handleAttackQuery}
                  onKeyPress={handleSearch}
                  loading={loading}
                  style={{ width: '320px' }}
                  size="large"
                  enterButton={
                    <Button 
                      type="primary" 
                      size="large"
                      icon={<SearchOutlined />}
                      loading={loading}
                      style={{
                        transition: 'all 0.3s ease',
                        transform: loading ? 'scale(0.95)' : 'scale(1)'
                      }}
                    >
                      {loading ? '查询中...' : '查询'}
                    </Button>
                  }
                />
            </Form.Item>
          </Form>
        </Card>

        {/* 结果区域 */}
        {hasSearched && (
          <Card
            style={{ border: '1px solid #e8e8e8' }}
            bodyStyle={{ padding: '20px' }}
          >
            {attackLogs.length > 0 && (
              <div style={{ 
                marginBottom: '20px', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '12px 16px',
                background: '#f5f5f5',
                borderRadius: '6px'
              }}>
                <div style={{ 
                  color: '#595959', 
                  fontSize: '14px'
                }}>
                  总计 <strong>{attackLogs.length}</strong> 条记录，
                  当前显示第 <strong>{indexOfFirstLog + 1}</strong> - <strong>{Math.min(indexOfLastLog, attackLogs.length)}</strong> 条
                </div>
                <Button
                  danger
                  icon={<FilePdfOutlined />}
                  onClick={handleExportClick}
                  loading={exportLoading}
                >
                  {exportLoading ? '生成中...' : '生成报告'}
                </Button>
              </div>
            )}
            
            {attackLogs.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '50px 20px', 
                color: '#8c8c8c',
                background: '#fafafa',
                borderRadius: '6px',
                border: '1px dashed #d9d9d9'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', color: '#d9d9d9' }}>🔍</div>
                <div style={{ fontSize: '16px', marginBottom: '8px', color: '#595959' }}>
                  未找到 <strong style={{ color: '#262626' }}>{ip}</strong> 的攻击日志
                </div>
                <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                  该IP地址可能没有遭受攻击，或者攻击记录已过期
                </div>
              </div>
            ) : (
              <>
                <Table
                  columns={columns}
                  dataSource={currentLogs}
                  pagination={false}
                  scroll={{ x: 1800 }}
                  size="middle"
                  bordered
                  rowKey={(record, index) => index}
                  style={{ borderRadius: '6px', overflow: 'hidden' }}
                />
                
                {totalPages > 1 && (
                  <div style={{ 
                    marginTop: '20px', 
                    textAlign: 'center',
                    padding: '16px',
                    background: '#fafafa',
                    borderRadius: '6px'
                  }}>
                    <Pagination
                      current={currentPage}
                      total={attackLogs.length}
                      pageSize={logsPerPage}
                      onChange={handlePageChange}
                      showSizeChanger={false}
                      showQuickJumper
                      showTotal={(total, range) => 
                        `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`
                      }
                    />
                  </div>
                )}
              </>
            )}
          </Card>
        )}

        {/* 导出确认弹窗 */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ExclamationCircleOutlined style={{ color: '#faad14' }} />
              <span>确认生成报告</span>
            </div>
          }
          open={showExportModal}
          onCancel={() => setShowExportModal(false)}
          footer={[
            <Button key="cancel" onClick={() => setShowExportModal(false)}>
              取消
            </Button>,
            <Button key="confirm" type="primary" danger onClick={handleExportConfirm}>
              确认生成
            </Button>
          ]}
          width={500}
        >
          <div style={{ padding: '16px 0' }}>
            <p style={{ marginBottom: '16px', fontSize: '16px' }}>您确定要生成攻击日志报告吗？</p>
            <div style={{ 
              background: '#fff7e6', 
              border: '1px solid #ffd591',
              borderRadius: '6px',
              padding: '16px'
            }}>
              <p style={{ marginBottom: '12px', color: '#d48806', fontWeight: '500' }}>
                ⚠️ 重要提示：
              </p>
              <ul style={{ 
                margin: 0, 
                paddingLeft: '20px', 
                color: '#8c8c8c',
                fontSize: '14px',
                lineHeight: '1.6'
              }}>
                <li>本报告属于内部文件，请勿对外传播</li>
                <li>报告包含真实性证明，可作为证据提交公安机关</li>
                <li>请妥善保管报告文件，避免泄露</li>
                <li>如需要提交给第三方，请先获得相关授权</li>
              </ul>
            </div>
          </div>
        </Modal>
      </div>
      
      {/* 简化的自定义样式 */}
      <style jsx>{`
        .ant-table-thead > tr > th {
          background-color: #fafafa;
          color: #262626;
          font-weight: 500;
          border-bottom: 2px solid #e8e8e8;
        }
        .ant-table-tbody > tr:hover > td {
          background-color: #f5f5f5;
        }
        .ant-table-tbody > tr:nth-child(even) {
          background-color: #fdfdfd;
        }
      `}</style>
    </div>
  );
};

export default UserAttackQuery;
